import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'


const Wrapper = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
`

const InputWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  height: 44px;
  padding: 10px 14px;
  cursor: pointer;
  border-radius: 8px;
  border: 0.909px solid #D1D5DC;
  background: #FFF;
`

const Input = styled.input`
  width: 100%;
  flex-shrink: 0;
  align-self: stretch;
  border: none;
  outline: none;
  font-family: Rubik;
  font-size: 14px;
  color: #0F172A;
  &::placeholder {
    color: #94A3B8;
    font-family: Rubik;
    font-size: 14px;
  }
`
// The calendar is rendered into document.body rather than next to its input:
// as an absolutely positioned child it was clipped by any ancestor that hides
// its overflow (e.g. the protocols advanced-search panel), which cut the month
// in half and made the days below the fold unclickable. Fixed positioning also
// keeps it above dialogs, which sit at z-index 1300.
const CalendarWrapper = styled.div`
  position: fixed;
  z-index: 1500;
  background: white;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
`
const ContainerValue = styled.div`
  display: flex;
  height: 35px;
  padding: 8px 12px 8px 12px;
  justify-content: flex-end;
  align-items: center;
  align-self: stretch;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.30);
`

const StyledDatePicker = styled(DatePicker)`
  background-color: green !important;

  .MuiPickersDay-daySelected {
    background-color: green !important;
  }
`

export default function DateInputWithCalendar({
  value,
  onChange,
  locale,
  disabled = false,
  icon = null,
  fontSize = '16px',
  inputBg = 'white',
  placeholder = ''
}: any) {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<any>(null)
  const popupRef = useRef<any>(null)
  // null until measured, so the popup is never painted at the wrong spot first.
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  const GAP = 6

  const reposition = useCallback(() => {
    const anchor = anchorRef.current
    const popup = popupRef.current
    if (!anchor || !popup) return
    const a = anchor.getBoundingClientRect()
    const p = popup.getBoundingClientRect()

    // Below the field, or above it when there is no room left underneath.
    let top = a.bottom + GAP
    if (top + p.height > window.innerHeight && a.top - GAP - p.height >= 0) {
      top = a.top - GAP - p.height
    }
    top = Math.max(GAP, Math.min(top, window.innerHeight - p.height - GAP))

    // Line the popup up with the edge the language starts from.
    const rtl = window.getComputedStyle(anchor).direction === 'rtl'
    let left = rtl ? a.right - p.width : a.left
    left = Math.max(GAP, Math.min(left, window.innerWidth - p.width - GAP))

    setPos({ top, left })
  }, [])

  useLayoutEffect(() => {
    if (!open) { setPos(null); return }
    reposition()
    // 'true' so the popup follows a scroll in any container, not just the page.
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open, reposition])

  // Replaces ClickAwayListener: the popup lives outside this component's DOM
  // now, so a click inside it would have counted as a click away and closed the
  // calendar before the date registered.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: any) => {
      if (anchorRef.current?.contains(e.target)) return
      if (popupRef.current?.contains(e.target)) return
      setOpen(false)
    }
    const onKeyDown = (e: any) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const formatDate = (val: any) => {
    const date = new Date(val)

    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const dd = String(date.getDate()).padStart(2, '0')
    const yyyy = date.getFullYear();

    return `${mm}/${dd}/${yyyy}`
  }

  const formatDateMMDDYYYY = (val: any) => {
    const date = new Date(val)

    const mm = String(date.getUTCMonth() + 1).padStart(2, "0")
    const dd = String(date.getUTCDate()).padStart(2, "0")
    const yyyy = date.getUTCFullYear()

    return `${mm}/${dd}/${yyyy}`
  }

  return (
    <>
    {disabled ? <ContainerValue>{value ? (value instanceof Date && !isNaN(value) ? formatDate(value) : formatDateMMDDYYYY(value)) : ''}</ContainerValue> : 
    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
      <Wrapper ref={anchorRef}>
        <InputWrapper onClick={() => setOpen(prev => !prev)}>
          {icon && icon}
          <Input
            readOnly
            value={value ? value instanceof Date && !isNaN(value) ? formatDate(value) : formatDateMMDDYYYY(value) : ''}
            placeholder={placeholder}
            disabled={disabled}
            style={{ fontSize, background: inputBg, textAlign: 'left' }}
          />
        </InputWrapper>

        {open && !disabled && ReactDOM.createPortal(
          <CalendarWrapper
            ref={popupRef}
            style={{
              top: pos ? pos.top : 0,
              left: pos ? pos.left : 0,
              visibility: pos ? 'visible' : 'hidden',
            }}
          >
            <StyledDatePicker
              autoOk
              value={value}
              onChange={date => {
                onChange(date)
                setOpen(false)
              }}
              variant="static"
              views={['year', 'month', 'date']}
              hiddenLabel
            //disableToolbar
            />
          </CalendarWrapper>,
          document.body
        )}
      </Wrapper>
    </MuiPickersUtilsProvider>}
    </>
  )
}
